import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Step } from "semantic-ui-react";
import { FormTabErrors } from "../FormTabErrors";
import { SectionCompletionBar } from "../SectionCompletionBar";
import { useFormNavigation } from "../../hooks";

export const FormSteps = ({
  sections,
  activeStep,
  onTabChange,
  tabsLocked = false,
}) => {
  const scrollContainerRef = useRef(null);
  const { hasBeenSavedInSession, isActive, handleClick, handleKeyDown } =
    useFormNavigation({
      activeStep,
      onTabChange,
    });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const steps = container.querySelectorAll(".step");
    const activeStepEl = steps[activeStep];
    if (activeStepEl) {
      const containerRect = container.getBoundingClientRect();
      const stepRect = activeStepEl.getBoundingClientRect();
      const scrollLeft =
        stepRect.left -
        containerRect.left +
        container.scrollLeft -
        containerRect.width / 2 +
        stepRect.width / 2;

      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, [activeStep]);

  return (
    <div
      className="form-steps-container"
      ref={scrollContainerRef}
      data-testid="form-steps-container"
    >
      <Step.Group
        fluid
        size="mini"
        unstackable
        role="tablist"
        data-testid="form-steps"
      >
        {sections.map((section, index) => (
          <Step
            id={`form-step-${section.key}`}
            key={section.key}
            active={isActive(index)}
            completed={index < activeStep}
            onClick={() => handleClick(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            disabled={tabsLocked && !isActive(index)}
            link
            role="tab"
            aria-selected={isActive(index)}
            tabIndex={isActive(index) ? 0 : -1}
            data-testid={`form-step-${section.key}`}
          >
            <Step.Content>
              <Step.Title>
                {hasBeenSavedInSession && (
                  <FormTabErrors includesPaths={section.includesPaths || []} />
                )}
                {section.label}
              </Step.Title>
            </Step.Content>
            <SectionCompletionBar
              includesPaths={section.includesPaths || []}
              sectionCompletion={section.sectionCompletion}
              sectionCompletionThreshold={section.sectionCompletionThreshold}
            />
          </Step>
        ))}
      </Step.Group>
    </div>
  );
};

FormSteps.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      includesPaths: PropTypes.array,
      saveOnTabChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      sectionCompletion: PropTypes.func,
      sectionCompletionThreshold: PropTypes.number,
      /** (formik, reduxState) => boolean — when truthy on the active section, tab navigation is blocked. */
      lockTabChange: PropTypes.func,
      /** component({ record, formConfig, activeStep, next, back, initialRecord }) => ReactNode */
      component: PropTypes.func.isRequired,
    })
  ),
  activeStep: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  tabsLocked: PropTypes.bool,
};

export default FormSteps;
